import { useCustomViewBlockContext } from '@lighthouse/block'
import { CollapseBlock } from '@lighthouse/block/src/blocks'
import type { CollapseBlockAbstract, RichTextContentProtocol, VariableADTvalue } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import type { RichTextEditorProps } from '@lighthouse/shared'
import { fieldConvertValue, useApplicationContext } from '@lighthouse/shared'
import type { FC } from 'react'
import React, { useCallback, useMemo } from 'react'

import { usePageDataQueryVariableLabel } from '@/hooks/usePageDataQueryVariableLabel'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface CollapseBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: CollapseBlockAbstract
}

const CollapseBlockController: FC<CollapseBlockControllerProps> = props => {
    const { blockData } = props
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()

    const allRichtext = useMemo(
        () =>
            blockData.config.list.reduce<RichTextContentProtocol[]>((p, c) => {
                if (c.introduction) {
                    p.push(c.introduction)
                }
                if (c.subTitle) {
                    p.push(c.subTitle)
                }
                if (c.title) {
                    p.push(c.title)
                }

                return p
            }, []),
        [blockData.config.list]
    )
    const { record } = useCustomViewBlockContext()
    const getPageDataQueryVariableLabel = usePageDataQueryVariableLabel()
    const parseVariable = useParseVariableWithSubscription(allRichtext)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value, {
                viewRecord: record
            })
            if (!field) {
                if (value.type === VariableType.PAGE_DATA_QUERY) {
                    return getPageDataQueryVariableLabel(value)
                }
                return ''
            }

            return fieldConvertValue(field, { personOptions, roleOptions, departmentOptions })
        },
        [departmentOptions, getPageDataQueryVariableLabel, parseVariable, personOptions, record, roleOptions]
    )

    const config: RichTextEditorProps['config'] = useMemo(
        () => ({
            mode: 'text-style',
            variable: {
                renderLabel: v => parseVariable2String(v.attrs.value)
            }
        }),
        [parseVariable2String]
    )

    return <CollapseBlock {...props} config={config} />
}

export default CollapseBlockController
